import { BufferAttribute, BufferGeometry, Vector3 } from 'three';
import * as BufferGeometryUtils from 'three-stdlib';

type TypedArray =
    | Int8Array
    | Uint8Array
    | Uint8ClampedArray
    | Int16Array
    | Uint16Array
    | Int32Array
    | Uint32Array
    | Float32Array
    | Float64Array;

interface EdgeSplitToGroupsResult {
    splitGroup: number[];
    currentGroup: number[];
}

interface SplitIndexes {
    original: number;
    indexes: number[];
}

class EdgeSplitModifierMergeTolerance {
    private A = new Vector3();
    private B = new Vector3();
    private C = new Vector3();

    private positions: ArrayLike<number> = [];
    private normals: Float32Array = new Float32Array();
    private indexes: ArrayLike<number> = [];
    private pointToIndexMap: number[][] = [];
    private splitIndexes: SplitIndexes[] = [];
    private oldNormals: ArrayLike<number> = [];

    constructor() {}

    private computeNormals = (): void => {
        this.normals = new Float32Array(this.indexes.length * 3);

        for (let i = 0; i < this.indexes.length; i += 3) {
            let index = this.indexes[i];

            this.A.set(this.positions[3 * index], this.positions[3 * index + 1], this.positions[3 * index + 2]);

            index = this.indexes[i + 1];
            this.B.set(this.positions[3 * index], this.positions[3 * index + 1], this.positions[3 * index + 2]);

            index = this.indexes[i + 2];
            this.C.set(this.positions[3 * index], this.positions[3 * index + 1], this.positions[3 * index + 2]);

            this.C.sub(this.B);
            this.A.sub(this.B);

            const normal = this.C.cross(this.A).normalize();

            for (let j = 0; j < 3; j++) {
                this.normals[3 * (i + j)] = normal.x;
                this.normals[3 * (i + j) + 1] = normal.y;
                this.normals[3 * (i + j) + 2] = normal.z;
            }
        }
    };

    private mapPositionsToIndexes = (): void => {
        this.pointToIndexMap = Array(this.positions.length / 3) as number[][];

        for (let i = 0; i < this.indexes.length; i++) {
            const index = this.indexes[i];

            if (this.pointToIndexMap[index] == null) {
                this.pointToIndexMap[index] = [];
            }

            this.pointToIndexMap[index].push(i);
        }
    };

    private edgeSplitToGroups = (indexes: number[], cutOff: number, firstIndex: number): EdgeSplitToGroupsResult => {
        this.A.set(this.normals[3 * firstIndex], this.normals[3 * firstIndex + 1], this.normals[3 * firstIndex + 2]).normalize();

        const result: EdgeSplitToGroupsResult = {
            splitGroup: [],
            currentGroup: [firstIndex],
        };

        for (const j of indexes) {
            if (j !== firstIndex) {
                this.B.set(this.normals[3 * j], this.normals[3 * j + 1], this.normals[3 * j + 2]).normalize();

                if (this.B.dot(this.A) < cutOff) {
                    result.splitGroup.push(j);
                } else {
                    result.currentGroup.push(j);
                }
            }
        }

        return result;
    };

    private edgeSplit = (indexes: number[], cutOff: number, original: number | null = null): void => {
        if (indexes.length === 0) return;

        const groupResults: EdgeSplitToGroupsResult[] = [];

        for (const index of indexes) {
            groupResults.push(this.edgeSplitToGroups(indexes, cutOff, index));
        }

        let result = groupResults[0];

        for (const groupResult of groupResults) {
            if (groupResult.currentGroup.length > result.currentGroup.length) {
                result = groupResult;
            }
        }

        if (original != null) {
            this.splitIndexes.push({
                original,
                indexes: result.currentGroup,
            });
        }

        if (result.splitGroup.length) {
            this.edgeSplit(result.splitGroup, cutOff, original || result.currentGroup[0]);
        }
    };

    /**
     * Modifies the given geometry by splitting its edges.
     *
     * @param {BufferGeometry} geometry - The geometry to modify.
     * @param {number} [cutOffAngle=0.785398] - The cut-off angle in radians for edge splitting (in deg: 45).
     * @param {boolean} [tryKeepNormals=true] - Whether to try keeping the original normals.
     * @param {number} [mergeTolerance=0.1] - The merge tolerance for vertices. A value of 0 will skip merging vertices (a missing geometry.index could cause problems down the line, though)
     * @returns {BufferGeometry} - The modified geometry.
     */
    public modify = (
        geometry: BufferGeometry,
        cutOffAngle: number = 0.785398,
        tryKeepNormals: boolean = true,
        mergeTolerance: number = 0.1,
    ): BufferGeometry => {
        let hadNormals = false;

        if (geometry.attributes.normal) {
            hadNormals = true;

            geometry = geometry.clone();

            if (tryKeepNormals === true && geometry.index !== null) {
                this.oldNormals = geometry.attributes.normal.array;
            }

            geometry.deleteAttribute('normal');
        }

        if (typeof mergeTolerance === 'number' && mergeTolerance > 0 && geometry.index === null) {
            if (BufferGeometryUtils === undefined) {
                throw 'THREE.EdgeSplitModifier relies on BufferGeometryUtils';
            }

            geometry = BufferGeometryUtils.mergeVertices(geometry, mergeTolerance);
        }

        this.indexes = (geometry.index as BufferAttribute).array;
        this.positions = geometry.getAttribute('position').array;

        this.computeNormals();
        this.mapPositionsToIndexes();

        this.splitIndexes = [];

        for (const vertexIndexes of this.pointToIndexMap) {
            this.edgeSplit(vertexIndexes, Math.cos(cutOffAngle) - 0.001);
        }

        const newAttributes: {
            [key: string]: BufferAttribute;
        } = {};
        for (const name of Object.keys(geometry.attributes)) {
            const oldAttribute = geometry.attributes[name];
            // @ts-expect-error ...
            const newArray = new oldAttribute.array.constructor(
                (this.indexes.length + this.splitIndexes.length) * oldAttribute.itemSize,
            ) as TypedArray;

            newArray.set(oldAttribute.array);
            newAttributes[name] = new BufferAttribute(newArray, oldAttribute.itemSize, oldAttribute.normalized);
        }

        const newIndexes = new Uint32Array(this.indexes.length);
        newIndexes.set(this.indexes);

        for (let i = 0; i < this.splitIndexes.length; i++) {
            const split = this.splitIndexes[i];
            const index = this.indexes[split.original];

            for (const attribute of Object.values(newAttributes)) {
                for (let j = 0; j < attribute.itemSize; j++) {
                    attribute.array[(this.indexes.length + i) * attribute.itemSize + j] = attribute.array[index * attribute.itemSize + j];
                }
            }

            for (const j of split.indexes) {
                newIndexes[j] = this.indexes.length + i;
            }
        }

        geometry = new BufferGeometry();
        geometry.setIndex(new BufferAttribute(newIndexes, 1));

        for (const name of Object.keys(newAttributes)) {
            geometry.setAttribute(name, newAttributes[name]);
        }

        if (hadNormals) {
            geometry.computeVertexNormals();

            if (this.oldNormals !== null) {
                const changedNormals = new Array(this.oldNormals.length / 3).fill(false);

                for (const splitData of this.splitIndexes) changedNormals[splitData.original] = true;

                for (let i = 0; i < changedNormals.length; i++) {
                    if (changedNormals[i] === false) {
                        for (let j = 0; j < 3; j++) {
                            geometry.attributes.normal.array[3 * i + j] = this.oldNormals[3 * i + j];
                        }
                    }
                }
            }
        }

        return geometry;
    };
}

export { EdgeSplitModifierMergeTolerance };
